import {Action, Card, DataStatus} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {assoc} from 'ramda';

import {CarisServiceRequestState, useGetVehicleCarisServiceRequestsQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, Main} from '@dms/shared';

import {buildArray} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {CarisServiceRequestCard} from './components/CarisServiceRequestCard';
import {CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE} from './constants/CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE';
import {useCarisServiceRequestPermissions} from './hooks/useCarisServiceRequestPermissions';
import {openCreateCarisServiceRequestDialog} from './utils/openCreateCarisServiceRequestDialog';

const ACTIVE_STATES: CarisServiceRequestState[] = ['NEW', 'ACCEPTED'];
const HISTORY_GRID_HEIGHT = 100;

interface CarisServiceRequestsProps {
  maximization?: EagGridMaximization;
  vehicleId: string;
}

export function CarisServiceRequests(props: CarisServiceRequestsProps) {
  const carisServiceRequestPermissions = useCarisServiceRequestPermissions(props.vehicleId);

  const {data, isLoading, isError} = useGetVehicleCarisServiceRequestsQuery({
    vehicleId: props.vehicleId,
    state: ACTIVE_STATES,
  });

  const activeServiceRequests = data?.items ?? [];

  // The backend requires this filter on every request; assoc overwrites any user value.
  const queryModifier = assoc('vehicleId', props.vehicleId);

  return (
    <Main data-testid={testIds.vehicles.carisServiceRequests('page')}>
      <VStack spacing={4}>
        <Card
          title={i18n.t('entity.carisServiceRequest.labels.activeRequests')}
          actions={buildArray<Action>().when(carisServiceRequestPermissions.canCreate, {
            type: 'button',
            variant: 'link',
            title: i18n.t('entity.carisServiceRequest.actions.create'),
            leftIcon: 'content/add_circle',
            onClick: () => openCreateCarisServiceRequestDialog(props.vehicleId),
            'data-testid': testIds.vehicles.carisServiceRequests('create'),
          })}
          data-testid={testIds.vehicles.carisServiceRequests('activeRequests')}
        >
          <DataStatus
            isLoading={isLoading}
            isError={isError}
            isEmpty={activeServiceRequests.length === 0}
            emptyMessage={i18n.t('entity.carisServiceRequest.labels.noActiveRequests')}
          >
            <VStack spacing={4}>
              {activeServiceRequests.map((serviceRequest) => (
                <CarisServiceRequestCard
                  key={serviceRequest.id}
                  serviceRequest={serviceRequest}
                  vehicleId={props.vehicleId}
                  canCancel={carisServiceRequestPermissions.canCancel}
                  data-testid={testIds.vehicles.carisServiceRequests(
                    `serviceRequest-${serviceRequest.id}`
                  )}
                />
              ))}
            </VStack>
          </DataStatus>
        </Card>
        <Card
          title={i18n.t('general.labels.history')}
          data-testid={testIds.vehicles.carisServiceRequests('historyCard')}
        >
          <Box height={HISTORY_GRID_HEIGHT}>
            <DmsDataGrid
              maximization={props.maximization}
              key={`${CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE}-${props.vehicleId}`}
              gridCode={CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE}
              queryModifier={queryModifier}
              // The legacy grid loads a saved preset's query without queryModifier, so presets
              // shared between vehicles could show the service requests of another vehicle.
              presetInstanceId={`${CARIS_SERVICE_REQUEST_HISTORY_GRID_CODE}-${props.vehicleId}`}
              disableAllActions
              data-testid={testIds.vehicles.carisServiceRequests('history')}
            />
          </Box>
        </Card>
      </VStack>
    </Main>
  );
}
